import { createSlice } from '@reduxjs/toolkit';
import { confirmPasswordReset, requestPasswordReset } from './passwordResetThunks';
import type { PasswordResetErrorCode, PasswordResetState } from './passwordResetTypes';
import { getBusinessAuthMessage } from '@/features/auth';

const initialState: PasswordResetState = {
  step: 'request',
  email: null,
  isRequesting: false,
  isConfirming: false,
  error: null,
  errorCode: null,
};

function getRejectedPayload(payload: unknown): { message?: string; retryAfterSeconds?: number } {
  return typeof payload === 'object' && payload ? (payload as { message?: string; retryAfterSeconds?: number }) : {};
}

const passwordResetSlice = createSlice({
  name: 'passwordReset',
  initialState,
  reducers: {
    resetPasswordResetState: () => initialState,
    clearPasswordResetError: (state) => {
      state.error = null;
      state.errorCode = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestPasswordReset.pending, (state) => {
        state.isRequesting = true;
        state.error = null;
        state.errorCode = null;
      })
      .addCase(requestPasswordReset.fulfilled, (state, action) => {
        state.isRequesting = false;
        state.step = 'confirm';
        state.email = action.meta.arg.email.trim();
      })
      .addCase(requestPasswordReset.rejected, (state, action) => {
        const payload = getRejectedPayload(action.payload);
        state.isRequesting = false;
        state.error = getBusinessAuthMessage(
          payload.message,
          action.error.message || 'Unable to request a password reset',
        );
        state.errorCode = (payload.message as PasswordResetErrorCode) ?? null;
      })
      .addCase(confirmPasswordReset.pending, (state) => {
        state.isConfirming = true;
        state.error = null;
        state.errorCode = null;
      })
      .addCase(confirmPasswordReset.fulfilled, (state) => {
        state.isConfirming = false;
      })
      .addCase(confirmPasswordReset.rejected, (state, action) => {
        const payload = getRejectedPayload(action.payload);
        state.isConfirming = false;
        state.error = getBusinessAuthMessage(payload.message, action.error.message || 'Unable to reset the password');
        state.errorCode = (payload.message as PasswordResetErrorCode) ?? null;
      });
  },
});

export const { resetPasswordResetState, clearPasswordResetError } = passwordResetSlice.actions;
export default passwordResetSlice.reducer;
